import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/auth.types'
import { STORAGE_KEYS } from '@/utils/constants'
import { useBrandingStore } from '@/stores/branding'
import { usePermissionsStore } from '@/stores/permissions'
import api from '@/services/api'
import { authService } from '@/services/auth.service'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)
  const bootstrapped = ref(false)

  const isAuthenticated = computed(() => !!user.value)
  const isSuperAdmin = computed(() => user.value?.is_super_admin || false)
  const userSection = computed(() => user.value?.section)
  const displayName = computed(() => {
    const u = user.value
    if (!u) return 'User'
    return u.full_name?.trim() || u.display_name?.trim() || u.username || 'User'
  })

  /** Remove legacy localStorage tokens from pre-cookie auth. */
  const clearLegacyTokens = () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.USER_DATA)
  }

  const bootstrap = async () => {
    loading.value = true
    clearLegacyTokens()
    try {
      const profile = await authService.getCurrentUser()
      user.value = profile
      await useBrandingStore().fetchDisplayBranding()
      await usePermissionsStore().fetchPermissions({ force: true })
    } catch {
      user.value = null
    } finally {
      loading.value = false
      bootstrapped.value = true
    }
  }

  const login = async (username: string, password: string) => {
    loading.value = true
    try {
      const response = await api.post('/api/v1/auth/login', { username, password })
      const data = response.data
      user.value = data.user
      clearLegacyTokens()
      await useBrandingStore().fetchDisplayBranding()
      await usePermissionsStore().fetchPermissions({ force: true })
      return data
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosErr = error as {
          status?: number
          code?: string
          errors?: Record<string, string[]>
          response?: { status?: number; data?: { message?: string; detail?: string } }
        }
        const body = axiosErr.response?.data
        const msg = body?.message || body?.detail || ''
        if (msg === 'tenant_suspended') {
          const err = new Error('tenant_suspended') as Error & { code?: string }
          err.code = 'tenant_suspended'
          throw err
        }
        if (msg) {
          // Keep status/code/errors: a bare Error is read as status 0 ("network error") downstream.
          throw Object.assign(new Error(msg), {
            status: axiosErr.status ?? axiosErr.response?.status,
            code: axiosErr.code,
            errors: axiosErr.errors,
          })
        }
      }
      console.error('Login error:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    user.value = null
    clearLegacyTokens()
    useBrandingStore().clear()
    usePermissionsStore().clearPermissions()
  }

  const refreshUser = async () => {
    if (!user.value) return

    loading.value = true
    try {
      user.value = await authService.getCurrentUser()
      await useBrandingStore().fetchDisplayBranding()
      await usePermissionsStore().fetchPermissions({ force: true })
    } catch (error) {
      console.error('Refresh user error:', error)
      logout()
    } finally {
      loading.value = false
    }
  }

  const checkAuth = async () => {
    if (!bootstrapped.value) {
      await bootstrap()
      return
    }
    if (!user.value) {
      await bootstrap()
    }
  }

  return {
    user,
    loading,
    bootstrapped,
    isAuthenticated,
    isSuperAdmin,
    userSection,
    displayName,
    bootstrap,
    login,
    logout,
    refreshUser,
    checkAuth,
  }
})
